import React, { useEffect, useId, useReducer, useState } from 'react';
import { Maximize, Share, SquarePlus, Zap } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useTranslations } from '../translations';
import { hasCurrentCookieNotice, subscribeAnalyticsConsent } from '../services/analyticsConsent';
import {
  OPEN_INSTALL_EVENT,
  clearInstallPrompt,
  isIosDevice,
  isMarkedInstalled,
  isStandalone,
  setMarkedInstalled,
  type InstallPromptEvent,
} from '../services/appInstall';
import './InstallAppBanner.css';

type InstallMode = 'native' | 'ios';

export const DISMISSAL_STORAGE_KEY = 'roade-install-prompt-v4';
const DAY_MS = 24 * 60 * 60 * 1000;
// Each "later" waits longer before asking again; after the third, the banner comes back every 60 days.
export const SNOOZE_DAYS = [3, 14, 60];
const MOBILE_INSTALL_QUERY = '(max-width: 1024px)';

interface DismissalState {
  count: number;
  at: number;
}

const matchesMediaQuery = (query: string) => (
  typeof window.matchMedia === 'function' && window.matchMedia(query).matches
);

const readDismissal = (): DismissalState | null => {
  try {
    const value = JSON.parse(localStorage.getItem(DISMISSAL_STORAGE_KEY) ?? 'null');
    return value && Number.isFinite(value.count) && Number.isFinite(value.at) ? value : null;
  } catch {
    return null;
  }
};

const isSnoozed = () => {
  const dismissal = readDismissal();
  if (!dismissal) return false;
  const days = SNOOZE_DAYS[Math.min(dismissal.count, SNOOZE_DAYS.length) - 1];
  return Date.now() - dismissal.at < days * DAY_MS;
};

const InstallAppBanner: React.FC = () => {
  const t = useTranslations();
  const titleId = useId();
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    matchesMediaQuery(MOBILE_INSTALL_QUERY)
  ));
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(
    () => window.__roadeInstallPrompt ?? null,
  );
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [snoozed, setSnoozed] = useState(isSnoozed);
  const [installed, setInstalled] = useState(isMarkedInstalled);
  // Opened from the footer: shown even when snoozed, on any screen size.
  const [requested, setRequested] = useState(false);
  // Wait for the cookie notice to be answered, so the two never stack on the first visit.
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  useEffect(() => subscribeAnalyticsConsent(rerender), []);

  const markInstalled = () => {
    localStorage.removeItem(DISMISSAL_STORAGE_KEY);
    setMarkedInstalled(true);
    setInstalled(true);
    setRequested(false);
  };

  const snooze = () => {
    const count = (readDismissal()?.count ?? 0) + 1;
    localStorage.setItem(DISMISSAL_STORAGE_KEY, JSON.stringify({ count, at: Date.now() }));
    setSnoozed(true);
    setRequested(false);
  };

  // The browser dialog can be opened only once per offer; a new offer comes with the next page load.
  const promptNativeInstall = async (promptEvent: InstallPromptEvent) => {
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    clearInstallPrompt();
    setInstallPrompt(null);
    if (choice.outcome === 'accepted') {
      markInstalled();
    } else {
      snooze();
    }
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform() || typeof window.matchMedia !== 'function') {
      return;
    }
    // Opened from the home screen: remember it, so the browser stops asking (Android shares this storage).
    if (isStandalone()) {
      markInstalled();
      return;
    }

    const mobileQuery = window.matchMedia(MOBILE_INSTALL_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
      setShowIosSteps(false);
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as InstallPromptEvent;
      window.__roadeInstallPrompt = promptEvent;
      setInstallPrompt(promptEvent);
      // The browser only offers installation when the app is not installed, e.g. after an uninstall.
      if (isMarkedInstalled()) setMarkedInstalled(false);
      setInstalled(false);
    };
    const handleInstalled = () => {
      clearInstallPrompt();
      setInstallPrompt(null);
      markInstalled();
    };
    const handleOpenRequest = () => {
      if (isIosDevice()) {
        setRequested(true);
        setShowIosSteps(true);
      } else if (window.__roadeInstallPrompt) {
        void promptNativeInstall(window.__roadeInstallPrompt);
      }
    };

    mobileQuery.addEventListener('change', handleViewportChange);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    window.addEventListener(OPEN_INSTALL_EVENT, handleOpenRequest);
    return () => {
      mobileQuery.removeEventListener('change', handleViewportChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      window.removeEventListener(OPEN_INSTALL_EVENT, handleOpenRequest);
    };
  }, []);

  const platformMode: InstallMode | null = (
    !installed
    && !Capacitor.isNativePlatform()
    && !isStandalone()
  )
    ? (isIosDevice() ? 'ios' : installPrompt ? 'native' : null)
    : null;
  const mode = requested || (isMobileViewport && !snoozed && hasCurrentCookieNotice())
    ? platformMode
    : null;

  const handleInstall = () => {
    if (mode === 'ios') {
      setShowIosSteps(true);
    } else if (installPrompt) {
      void promptNativeInstall(installPrompt);
    }
  };

  if (!mode) {
    return null;
  }

  return (
    <aside
      className={`install-app-banner${requested ? ' install-app-banner-requested' : ''}`}
      aria-labelledby={titleId}
    >
      <div className="install-app-banner-header">
        <img className="install-app-banner-app-icon" src="/icons/icon-192.png?v=15" alt="" />
        <div className="install-app-banner-heading">
          <strong id={titleId}>{showIosSteps ? t.installApp.iosStepsTitle : t.installApp.title}</strong>
          <span>{t.installApp.subtitle}</span>
        </div>
      </div>

      {showIosSteps ? (
        <ol className="install-app-banner-steps">
          <li>
            <Share aria-hidden="true" />
            <span>{t.installApp.iosStepShare}</span>
          </li>
          <li>
            <SquarePlus aria-hidden="true" />
            <span>{t.installApp.iosStepAdd}</span>
          </li>
          <li>
            <span className="install-app-banner-step-confirm" aria-hidden="true">{t.installApp.iosConfirmLabel}</span>
            <span>{t.installApp.iosStepConfirm}</span>
          </li>
        </ol>
      ) : (
        <ul className="install-app-banner-benefits">
          <li>
            <Zap aria-hidden="true" />
            <span>{t.installApp.benefitQuickAccess}</span>
          </li>
          <li>
            <Maximize aria-hidden="true" />
            <span>{t.installApp.benefitFullScreen}</span>
          </li>
        </ul>
      )}

      <div className="install-app-banner-actions">
        <button type="button" className="install-app-banner-later" onClick={snooze}>
          {t.installApp.later}
        </button>
        {showIosSteps ? (
          <button type="button" className="install-app-banner-action" onClick={markInstalled}>
            {t.installApp.iosDone}
          </button>
        ) : (
          <button type="button" className="install-app-banner-action" onClick={handleInstall}>
            {mode === 'ios' ? t.installApp.iosAction : t.installApp.install}
          </button>
        )}
      </div>
    </aside>
  );
};

export default InstallAppBanner;
